import { siteUrl } from "@/lib/seo";

type JsonLdValue =
	| string
	| number
	| boolean
	| null
	| JsonLdValue[]
	| { [key: string]: JsonLdValue };

export type JsonLdGraph = {
	"@context": "https://schema.org";
	"@graph"?: JsonLdValue[];
	[key: string]: JsonLdValue | undefined;
};

function jsonLdString(data: JsonLdGraph): string {
	return JSON.stringify(data).replace(/</g, "\\u003c");
}

export function JsonLd({ data }: { data: JsonLdGraph }) {
	return (
		<script
			type="application/ld+json"
			// biome-ignore lint/security/noDangerouslySetInnerHtml: JSON-LD must be emitted as a script payload, and the serialized value escapes HTML opener characters.
			dangerouslySetInnerHTML={{ __html: jsonLdString(data) }}
		/>
	);
}

export const organizationId = `${siteUrl}/#organization`;
export const websiteId = `${siteUrl}/#website`;
export const softwareId = `${siteUrl}/#software`;

export function buildCoreJsonLd(): JsonLdGraph {
	return {
		"@context": "https://schema.org",
		"@graph": [
			{
				"@type": "Organization",
				"@id": organizationId,
				name: "AgentFramer",
				url: siteUrl,
				logo: `${siteUrl}/logo.png`,
				description:
					"AgentFramer is an MCP-native media generation execution layer for AI agents.",
				sameAs: ["https://github.com/thijssmudde/agentframer"],
			},
			{
				"@type": "WebSite",
				"@id": websiteId,
				name: "AgentFramer",
				url: siteUrl,
				publisher: { "@id": organizationId },
			},
			buildSoftwareApplication(),
		],
	};
}

export function buildSoftwareApplication(): JsonLdValue {
	return {
		"@type": "SoftwareApplication",
		"@id": softwareId,
		name: "AgentFramer",
		applicationCategory: "DeveloperApplication",
		operatingSystem: "Web, MCP",
		url: siteUrl,
		description:
			"MCP-native image, video, and audio generation for Claude, Cursor, Windsurf, Codex, and other agent clients.",
		publisher: { "@id": organizationId },
		offers: {
			"@type": "AggregateOffer",
			name: "Workspace balance top-up",
			priceCurrency: "USD",
			lowPrice: 1,
			highPrice: 1000,
			url: `${siteUrl}/pricing`,
		},
	};
}

export function buildArticleJsonLd({
	path,
	title,
	description,
	datePublished,
	image,
}: {
	path: string;
	title: string;
	description: string;
	datePublished: string;
	image: string;
}): JsonLdGraph {
	const url = `${siteUrl}${path}`;

	return {
		"@context": "https://schema.org",
		"@type": "Article",
		"@id": `${url}#article`,
		mainEntityOfPage: url,
		headline: title,
		description,
		image,
		datePublished,
		dateModified: datePublished,
		author: { "@id": organizationId },
		publisher: { "@id": organizationId },
	};
}

export function buildFaqJsonLd(
	items: Array<{ question: string; answer: string }>,
): JsonLdGraph {
	return {
		"@context": "https://schema.org",
		"@type": "FAQPage",
		mainEntity: items.map((item) => ({
			"@type": "Question",
			name: item.question,
			acceptedAnswer: {
				"@type": "Answer",
				text: item.answer,
			},
		})),
	};
}

export function buildPricingJsonLd(): JsonLdGraph {
	return {
		"@context": "https://schema.org",
		"@graph": [
			buildSoftwareApplication(),
			{
				"@type": "Product",
				"@id": softwareId,
				name: "AgentFramer",
				description:
					"Hosted MCP media generation infrastructure for AI agents.",
				brand: { "@id": organizationId },
				offers: {
					"@type": "AggregateOffer",
					priceCurrency: "USD",
					lowPrice: 1,
					highPrice: 1000,
					url: `${siteUrl}/pricing`,
				},
			},
		],
	};
}

export function buildBreadcrumbJsonLd(pathname: string): JsonLdGraph | null {
	const cleanPath = pathname.replace(/\/$/, "");
	if (!cleanPath || cleanPath === "/") return null;

	const segments = cleanPath.split("/").filter(Boolean);
	const items = [
		{
			"@type": "ListItem",
			position: 1,
			name: "Home",
			item: siteUrl,
		},
		...segments.map((segment, index) => {
			const path = `/${segments.slice(0, index + 1).join("/")}`;
			return {
				"@type": "ListItem",
				position: index + 2,
				name: segment
					.split("-")
					.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
					.join(" "),
				item: `${siteUrl}${path}`,
			};
		}),
	];

	return {
		"@context": "https://schema.org",
		"@type": "BreadcrumbList",
		itemListElement: items,
	};
}
